/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Group} from '@mantine/core';
import { useSelector } from 'react-redux';
import { setFormConfig, setMdmCurrentStep, setSelectedCardId, updateCards } from '../../stateManagement/actions/mdmDashboard';
import CreateForm from '../createForm/CreateForm';
import ApiSelector from '../formBuilder/ApiSelector';
import { ChannelkartNetworkGet, META_DATA_BATCH_API } from '../../services/networkService';
import { metaDataBatchPayload, popupType } from '../../types/types';
import { getLob } from '../../services/authenticationService';
import { validateWithOtp } from '../../stateManagement/actions/validateOtpPopupActions';
import { manageUpdateAccessObj, } from '../manageUpdateAccess/manageUpdateAccessTypes';
import { validateMetaDataResponse } from '../../services/uiConfigService';
import { GenericPopUp } from '../../components/popup/genericPopUp';
import { Loader } from '../../components/loader/Loader';
import { ChevronLeft, ChevronRight } from 'tabler-icons-react';
import { openPopup } from '../../stateManagement/actions/popupActions';

interface Card {
  id: string;
  name: string;
  formId: string;
  excelTemplate: string;
  tableConfiguration: {
    columns: { id: string; label: string; columnName: string }[];
  };
  formConfiguration?: any;
}

interface ExcelColumns {
  [key: string]: string[];
}

interface CardState {
  step: 'form' | 'api';
  editFormConfig: any | null;
  apiSelectorPayload: {
    formSections: any[];
    meta?: {
      api: string;
      formName: string;
      buttonLabel: string;
      formId: string;
    };
  } | null;
  unmatchedLabels: string[];
  mandatoryLabels: string[];
}

const MdmFormSetUp = ({ excelMasterColumns, domainTypePrefix }: { excelMasterColumns: ExcelColumns; domainTypePrefix?: string; }) => {
  const cards = useSelector((state: any) => state.mdmDashboardReducer.cards);
  const selectedCardIdFromStore = useSelector((state: any) => state.mdmDashboardReducer.selectedCardId);
  const selectedCardId = selectedCardIdFromStore ?? cards?.[0]?.id ?? null;
  const formConfig = useSelector((state: any) => state.mdmDashboardReducer.formConfig);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [modifiedCards, setModifiedCards] = useState<Set<string>>(new Set());
  const [cardConfigMap, setCardConfigMap] = useState<Record<string, CardState>>({});
  const [popupMessage, setPopupMessage] = useState<string>("");
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  const getDefaultConfigForWidgetType = (widgetType: string, label: string) => {
    const baseConfig = {
      label: label,
      value: "",
      editable: false,
      required: false,
      description: "",
    };

    switch (widgetType) {
      case "text-input":
        return {
          ...baseConfig,
          hintText: "",
          inputType: {
            type: "options",
            values: ["text", "email", "password", "number", "tel", "url"],
            selected: ""
          },
          defaultText: "",
          maxCharacters: 0,
          regexValidation: "",
        };

      case "checkbox":
        return {
          ...baseConfig,
          options: [`${label} Option 1`, `${label} Option 2`],
          selectedValues: [],
        };

      case "radio":
        return {
          ...baseConfig,
          options: [`${label} Option 1`, `${label} Option 2`],
          selectedValue: "",
        };

      case "dropdown":
        return {
          ...baseConfig,
          options: [`${label} Option 1`, `${label} Option 2`],
          value: "",
          defaultOptions: "Option 1",
        };

      case "textarea":
        return {
          ...baseConfig,
          hintText: "",
          maxCharacters: 500,
          rows: 4,
        };

      case "date":
        return {
          ...baseConfig,
          dateFormat: "yyyy-mm-dd",
          minDate: "",
          maxDate: "",
        };

      default:
        return {
          ...baseConfig,
          hintText: "",
          inputType: {
            type: "options",
            values: ["text", "email", "password", "number", "tel", "url"],
            selected: ""
          },
          defaultText: "",
          maxCharacters: 0,
          regexValidation: "",
        };
    }
  };

  useEffect(() => {
    if (cards.length > 0 && !selectedCardId) {
      setSelectedCardId(cards[0].id);
    }
  }, [cards, selectedCardId]);

  useEffect(() => {
    if (selectedCardId) {
      const selectedCard = cards.find((card: Card) => card.id === selectedCardId);
      if (selectedCard && !cardConfigMap[selectedCardId]) {
        handleEditForm(selectedCard.formId, selectedCardId);
      }
    }
  }, [selectedCardId, cards, cardConfigMap]);

  const handleEditForm = async (cardFormId: string, cardId: string) => {
    console.log(formConfig)
    setLoading(true);
    let selectedForm = null;
    if (cardFormId) {
      try {
        const allForms = formConfig?.[0]?.value || [];
        selectedForm = allForms.find((form: any) => form.formId === cardFormId);
      } catch (err) {
        console.error("Failed to fetch form configuration:", err);
      }
    }

    if (!selectedForm) {
      const emptyFormId = crypto.randomUUID();
      const tableColumns = cards.find((card: any) => card.id === cardId)?.tableConfiguration?.columns || [];
      const columnMap = new Map<string, { columnName: string; id: string, label: string }>(
        tableColumns.map((col: any) => [col.columnName, { columnName: col.columnName, id: col.id, label: col.label }])
      );
      const excelColumns = Array.from(columnMap.values());
      const excelLabels = excelColumns.map(col => col.columnName);
      console.log("columnMap", columnMap);
      console.log("Table columns for card:", tableColumns);
      if (tableColumns.length > 0) {
        const widgets = tableColumns.map((col: any) => {
          const matchedColumn = columnMap.get(col.columnName);
          return {
            id: crypto.randomUUID(),
            columnName: matchedColumn?.columnName || col.columnName,
            config: getDefaultConfigForWidgetType("text-input", matchedColumn?.label || ""),
            widgetType: "text-input",
            mappedApiField: matchedColumn?.id || "",        
          };
        });
            
        const additionalSection = {
          id: crypto.randomUUID(),
          name: "Additional Fields",
          widgets,
        };
      
        const defaultForm = {
          api: "",
          sections: [additionalSection],
          formName: "Untitled Form",
          buttonLabel: "Submit",
          formId: emptyFormId,
        };
      
        const payload = {
          formSections: defaultForm.sections,
          meta: {
            api: "",
            formName: "Untitled Form",
            buttonLabel: "Submit",
            formId: emptyFormId,
          },
        };
      
        setCardConfigMap(prev => ({
          ...prev,
          [cardId]: {
            step: 'form',
            unmatchedLabels: excelLabels,
            mandatoryLabels: [],
            editFormConfig: {
              row: cards.find((card: { id: string }) => card.id === cardId),
              domainValues: [],
              initialForm: defaultForm,
            },
            apiSelectorPayload: payload,
          },
        }));
        setLoading(false);
        return;
      }
       else {
        const defaultEmptyForm = {
          api: "",
          sections: [
            {
              id: crypto.randomUUID(),
              name: "Untitled Section",
              widgets: [],
            },
          ],
          formName: "Untitled Form",
          buttonLabel: "Submit",
          formId: emptyFormId,
        };

        const payload = {
          formSections: defaultEmptyForm.sections,
          meta: {
            api: "",
            formName: "Untitled Form",
            buttonLabel: "Submit",
            formId: emptyFormId,
          },
        };

        setCardConfigMap(prev => ({
          ...prev,
          [cardId]: {
            step: 'form',
            unmatchedLabels: [],
            mandatoryLabels: [],
            editFormConfig: {
              row: cards.find((card: { id: string }) => card.id === cardId),
              domainValues: [],
              initialForm: defaultEmptyForm,
            },
            apiSelectorPayload: payload,
          },
        }));

        setLoading(false);
        return;
      }
    }

    const tableColumns = cards.find((card: any) => card.id === cardId)?.tableConfiguration?.columns || [];
    const columnMap = new Map<string, { columnName: string; id: string, label: string }>(
        tableColumns.map((col: any) => [col.columnName, { columnName: col.columnName, id: col.id, label: col.label }])
      );
    const excelColumns = Array.from(columnMap.values());
    const excelLabels = excelColumns.map(col => col.columnName);
    console.log("Excel labels for card:", excelLabels);
    const formLabels = selectedForm.sections?.flatMap((section: any) =>
      section.widgets.map((widget: { columnName?: string }) =>
        widget.columnName?.toLowerCase()?.trim()
      )
    ).filter(Boolean) || [];

    const unmatchedLabels = excelLabels.filter(col =>
      !formLabels.some((existing: string) => existing?.toLowerCase().trim() === col.toLowerCase().trim())
    );
    

    const filteredSections = selectedForm.sections?.map((section: any) => {
      const widgets = section.widgets.filter((widget: { columnName?: string }) =>
        excelLabels.some((label: string) =>
          widget.columnName?.toLowerCase().trim() === label.toLowerCase().trim()
        )
      );      
      return { ...section, widgets };
    }).filter((section: any) => section.widgets.length > 0) || [];

    console.log("currentcardstate", currentCardState);

    const createUnmatchedWidget = (col: string, widgetType: string = "text-input") => {
      const matchedColumn = columnMap.get(col);
      return {
        id: crypto.randomUUID(),
        columnName: matchedColumn?.columnName || col,
        config: getDefaultConfigForWidgetType(widgetType, matchedColumn?.label || ""),
        widgetType,
        mappedApiField: matchedColumn?.id || "", 
      };
    };
    let finalSections = [...filteredSections];

    if (unmatchedLabels.length > 0) {
      const unmatchedWidgets = unmatchedLabels.map(label => createUnmatchedWidget(label));
      const unmatchedSection = {
        id: crypto.randomUUID(),
        name: "Additional Fields",
        widgets: unmatchedWidgets
      };

      finalSections.push(unmatchedSection);
    }

    console.log("Final sections after adding unmatched labels:", finalSections)
    console.log("Column map for card:", columnMap);
    console.log("Table columns for card:", tableColumns);

    const mandatoryLabels = tableColumns
      .filter((col: { mandatory: any; }) => col.mandatory)
      .map((col: { columnName: string; }) => col.columnName?.toLowerCase()?.trim());

    console.log("Mandatory labels for card:", mandatoryLabels);

    finalSections = finalSections.map(section => ({
      ...section,
      widgets: section.widgets.map((widget: any) => {
        const matchedColumn = columnMap.get(widget.columnName);
        console.log(`Widget:`, widget, `columnName:`, widget.columnName, `matchedColumn:`, matchedColumn);
        if (matchedColumn) {
          return {
            ...widget,
            columnName: matchedColumn.columnName,
            mappedApiField: matchedColumn.id,
            config: {
              ...widget.config,
              label: matchedColumn.label,
            },
          };
        }
        return widget;
      }),
    }));


    console.log("Final sections after mapping columns:", finalSections);

    const initialForm = {
      api: selectedForm.api,
      sections: finalSections,
      formName: selectedForm.formName,
      buttonLabel: selectedForm.buttonLabel,
      formId: selectedForm.formId,
    };

    const payload = {
      formSections: finalSections,
      meta: {
        api: selectedForm.api,
        formName: selectedForm.formName,
        buttonLabel: selectedForm.buttonLabel,
        formId: selectedForm.formId,
      },
    };

    setCardConfigMap(prev => ({
      ...prev,
      [cardId]: {
        step: prev[cardId]?.step || 'form',
        unmatchedLabels,
        mandatoryLabels,
        editFormConfig: {
          row: cards.find((card: { id: string; }) => card.id === cardId),
          domainValues: [],
          initialForm,
        },
        apiSelectorPayload: payload
      },
    }));
    setLoading(false);
  };

  const handleSave = (formData: any, meta: any) => {
    if (!selectedCardId) return;

    setModifiedCards(prev => new Set([...Array.from(prev), selectedCardId]));

    const updatedCards = cards.map((card: Card) =>
      card.id === selectedCardId
        ? {
          ...card,
          formId: meta.formId || card.formId,
        }
        : card
    );

    updateCards(updatedCards);
    console.log("Updated cards after form submission:", updatedCards);
    const payload = {
      formSections: formData,
      meta: {
        api: meta.api,
        formName: meta.formName,
        buttonLabel: meta.buttonLabel,
        formId: meta.formId,
      },
    };

    setCardConfigMap(prev => ({
      ...prev,
      [selectedCardId]: {
        ...prev[selectedCardId],
        apiSelectorPayload: payload,
        step: 'api',
      },
    }));
  };

  const handleFormChange = (updatedSections: any[], updatedMeta?: any[]) => {
    if (!selectedCardId) return;

    setModifiedCards(prev => new Set(Array.from(prev).concat(selectedCardId)));

    setCardConfigMap(prev => {
      const currentCard = prev[selectedCardId] || {};
      const currentPayload = currentCard.apiSelectorPayload || {};
      const updatedMetaData = updatedMeta?.[0] || currentCard.apiSelectorPayload?.meta;

      const newState = {
        ...prev,
        [selectedCardId]: {
          ...currentCard,
          apiSelectorPayload: {
            ...currentPayload,
            formSections: updatedSections,
            meta: updatedMetaData
          },
          editFormConfig: currentCard.editFormConfig
            ? {
              ...currentCard.editFormConfig,
              initialForm: {
                ...currentCard.editFormConfig.initialForm,
                sections: updatedSections,
              },
            }
            : currentCard.editFormConfig,
        },
      };

      console.log("Updated card config map:", newState);
      console.log("Updated cardConfigMap:", cardConfigMap);
      return newState;
    });
  };

  const finalizeAndSubmit = async () => {
    console.log("modifiedCards:", Array.from(modifiedCards));
    console.log("cardConfigMap:", cardConfigMap);

    const modifiedCardsWithConfigs = Array.from(modifiedCards).filter(cardId =>
      cardConfigMap[cardId]?.apiSelectorPayload
    );

    console.log("Submitting configurations for modified cards:", modifiedCardsWithConfigs);
    const allFormConfigurations: { sections: any[]; formId: string; api: any; formName: any; buttonLabel: any; }[] = [];
    let validationErrors = [];
    if (selectedCardId && cardConfigMap[selectedCardId]?.apiSelectorPayload) {
      const card = cards.find((c: { id: any; }) => c.id === selectedCardId);
      const state = cardConfigMap[selectedCardId];
      const formSections = state?.apiSelectorPayload?.formSections || [];
      const sectionsWithIncompleteWidgets = formSections.filter(section => 
        section.widgets.some((widget: any) => 
          widget.widgetType === 'text-input' && !widget.config.inputType.selected
        )
      );
  
      if (sectionsWithIncompleteWidgets.length > 0) {
        const incompleteSectionNames = sectionsWithIncompleteWidgets.map(section => section.name).join(', ');
        const cardName = card?.name || `Card ${selectedCardId}`;
        validationErrors.push(`${cardName}: Please select an input type for all text-input fields in sections: ${incompleteSectionNames}.`);
      }
    }
  
    for (const cardId of modifiedCardsWithConfigs) {
      if (cardId === selectedCardId) continue; 
      const card = cards.find((c: { id: string; }) => c.id === cardId);
      const state = cardConfigMap[cardId];
      const formSections = state?.apiSelectorPayload?.formSections || [];
      const sectionsWithIncompleteWidgets = formSections.filter(section => 
        section.widgets.some((widget: any) => 
          widget.widgetType === 'text-input' && !widget.config.inputType.selected
        )
      );
  
      if (sectionsWithIncompleteWidgets.length > 0) {
        const incompleteSectionNames = sectionsWithIncompleteWidgets.map(section => section.name).join(', ');
        const cardName = card?.name || `Card ${selectedCardId}`;
        validationErrors.push(`${cardName}: Please select an input type for all text-input fields in sections: ${incompleteSectionNames}.`);
      }
    }

    if (validationErrors.length > 0) {
      openPopup("Alert", `Validation errors:\n${validationErrors.join('\n')}`);
      return;
    }

    let existingFormConfigs: any[] = formConfig?.[0]?.value || [];
    console.log("Using Redux form config as base:", existingFormConfigs);

    try {
      const name = domainTypePrefix ? `${domainTypePrefix}_form_configuration` : "admin_form_configuration";
      const response = await ChannelkartNetworkGet(
        `/metadata/clientconfig?name=${name}`
      );

      if (
        response.data.features &&
        response.data.features.length > 0 &&
        response.data.features[0].domainValues &&
        response.data.features[0].domainValues.length > 0
      ) {
        existingFormConfigs = response.data.features[0].domainValues[0]?.value || [];
        console.log("Using form configs from API:", existingFormConfigs);
      } else {
        console.log("API returned empty form config, using Redux state");
      }
    } catch (err) {
      console.error("Error fetching form config from API, using Redux state:", err);
    }

    const updatedFormConfigs = existingFormConfigs.map((formConfig: any) => {
      const card = cards.find((c: Card) => c.formId === formConfig.formId);
      if (!card) return formConfig;

      const cardId = card.id;
      const tableColumns = cards.find((card: any) => card.id === cardId)?.tableConfiguration?.columns || [];
      const columnMap = new Map<string, { columnName: string; id: string, label: string }>(
        tableColumns.map((col: any) => [col.columnName, { columnName: col.columnName, id: col.id, label: col.label }])
      );

      const updatedSections = formConfig.sections.map((section: any) => ({
        ...section,
        widgets: section.widgets.map((widget: any) => {
          const matchingColumn = columnMap.get(widget.columnName);
          if (matchingColumn) {
            return {
              ...widget,
              columnName: matchingColumn.columnName,
              mappedApiField: matchingColumn.id,
              config: {
                ...widget.config,
                label: matchingColumn.label,
              },
            };
          }
          return widget;
        }),
      }));

      return {
        ...formConfig,
        sections: updatedSections,
      };
    });

    for (const cardId of modifiedCardsWithConfigs) {
      const currentState = cardConfigMap[cardId];
      if (!currentState?.apiSelectorPayload) continue;

      let { formSections, meta } = currentState.apiSelectorPayload;
      console.log(`Processing modified card ${cardId} with sections:`, formSections);

      const tableColumns = cards.find((card: any) => card.id === cardId)?.tableConfiguration?.columns || [];
      const columnMap = new Map<string, { columnName: string; id: string; label: string }>(
        tableColumns.map((col: any) => [col.columnName, { columnName: col.columnName, id: col.id, label: col.label }])
      );

      formSections = formSections.map((section: any) => ({
        ...section,
        widgets: section.widgets.map((widget: any) => {
          const matchingColumn = columnMap.get(widget.columnName);
          if (matchingColumn) {
            return {
              ...widget,
              columnName: matchingColumn.columnName,
              mappedApiField: matchingColumn.id,
              config: {
                ...widget.config,
                label: matchingColumn.label,
              },
            };
          }
          return widget;
        }),
      }));

      const sectionsWithIncompleteWidgets = formSections.filter(section => 
        section.widgets.some((widget: any) => 
          widget.widgetType === 'text-input' && !widget.config.inputType.selected
        )
      );
      console.log(`Sections with incomplete widgets for card ${cardId}:`, sectionsWithIncompleteWidgets);

      if (sectionsWithIncompleteWidgets.length > 0) {
        const incompleteSectionNames = sectionsWithIncompleteWidgets.map(section => section.name).join(', ');
        const cardName = `Card ${cardId}`;
        validationErrors.push(`${cardName}: Please select an input type for all text-input fields in sections: ${incompleteSectionNames}.`);
      }

      const finalJsonSchema = {
        api: meta?.api || "",
        formName: meta?.formName || "",
        buttonLabel: meta?.buttonLabel || "",
        sections: formSections,
        formId: meta?.formId || crypto.randomUUID(),
      };

      allFormConfigurations.push(finalJsonSchema);
      console.log("Final JSON schema sections", finalJsonSchema.sections);
      console.log("Form ID for this configuration:", finalJsonSchema.formId);
    }

    console.log("All form configurations to be submitted:", allFormConfigurations);

    if (validationErrors.length > 0) {
      openPopup("Alert", `Validation errors:\n${validationErrors.join('\n')}`);
      return;
    }
    console.log("Validation errors:", validationErrors);
    const finalFormConfigs = [...updatedFormConfigs];
    allFormConfigurations.forEach(newFormConfig => {
      const index = finalFormConfigs.findIndex(
        (f: any) => f.formId === newFormConfig.formId
      );
      if (index !== -1) {
        finalFormConfigs[index] = newFormConfig;
        console.log(`Updated existing form config for formId: ${newFormConfig.formId}`);
      } else {
        finalFormConfigs.push(newFormConfig);
        console.log(`Added new form config for formId: ${newFormConfig.formId}`);
      }
    });

    const updatedCards = cards.map((card: Card) => {
      if (modifiedCards.has(card.id)) {
        const matchingConfig = allFormConfigurations.find(config => {
          const cardState = cardConfigMap[card.id];
          return cardState?.apiSelectorPayload?.meta?.formId === config.formId;
        });

        if (matchingConfig) {
          return {
            ...card,
            formId: matchingConfig.formId,
          };
        }
      }
      return card;
    });

    updateCards(updatedCards);

    console.log("Existing formConfig formIds:", finalFormConfigs.map((f: any) => f.formId));
    console.log("Final merged form configurations:", finalFormConfigs);

    const formConfigPayload = [
      {
        value: finalFormConfigs,
      },
    ];

    const finalMetaDataPayload: metaDataBatchPayload = {
      features: [
        {
          domainName: "clientconfig",
          domainType: domainTypePrefix ? `${domainTypePrefix}_form_configuration` : "admin_form_configuration",
          domainValues: formConfigPayload,
          lob: getLob(),
        },
        {
          domainName: "clientconfig",
          domainType: domainTypePrefix ? `${domainTypePrefix}_generic_mdm_configuration` : "admin_generic_mdm_configuration",
          domainValues: updatedCards,
          lob: getLob(),
        },
      ],
    };

    console.log("Final form configurations being sent:", finalFormConfigs);
    console.log("Updated card configurations (NO formConfig):", updatedCards);
    console.log("Form config payload structure:", formConfigPayload);
    console.log("Final metadata payload:", finalMetaDataPayload);

    validateWithOtp(
      async (verifiedUser?: manageUpdateAccessObj, verifyResponse?: any) => {
        if (verifyResponse.status === 200 || verifyResponse.status === 201) {
          const validationObj = validateMetaDataResponse(verifyResponse?.data?.features);
          if (validationObj.success) {
            setFormConfig(formConfigPayload);
            const submittedCardsCount = allFormConfigurations.length;
            setPopupMessage(
              `Successfully saved configurations`
            );
            setPopupType("Success");
            setOpenGenericModal(true);
            setModifiedCards(new Set());
          } else {
            setPopupMessage(validationObj.message);
            setPopupType("Error");
            setOpenGenericModal(true);
          }
        } else {
          setPopupMessage("Submission failed");
          setPopupType("Error");
          setOpenGenericModal(true);
        }
      },
      undefined,
      "all",
      META_DATA_BATCH_API,
      finalMetaDataPayload,
      "PUT"
    );
  };

  const handleBack = () => {
    if (!selectedCardId) return;
    const currentState = cardConfigMap[selectedCardId];
    if (currentState?.step === 'api') {
      setCardConfigMap(prev => ({
        ...prev,
        [selectedCardId]: {
          ...prev[selectedCardId],
          step: 'form',
        },
      }));
    } else {
      setMdmCurrentStep(2);
    }
  };

  const handleCardSelection = (cardFormId: string, cardId: string) => {
    handleEditForm(cardFormId, cardId);
  };

  const currentCardState = selectedCardId ? cardConfigMap[selectedCardId] : null;

  const memoizedInitialForm = useMemo(
    () => currentCardState?.editFormConfig?.initialForm,
    [currentCardState]
  );

  const filteredInitialForm = useMemo(() => {
    if (!memoizedInitialForm) return memoizedInitialForm;
    const sections = (memoizedInitialForm.sections || [])
      .map((section: any) => ({
        ...section,
        widgets: (section.widgets || []).filter((w: any) => w?.id && w?.mappedApiField),
      }));
    return { ...memoizedInitialForm, sections };
  }, [memoizedInitialForm]);


  return (
    <Box style={{ display: 'flex', height: '100%', padding: '0.5rem', gap: '20px' }}>
      {/* Sidebar */}
      <Box style={{ height: isSidebarOpen ? "100%" : "5%", width: isSidebarOpen ? 230 : 40, border: '1px solid rgb(204, 204, 204)', borderRadius: '6px' }}>

        {/* Toggle Icon */}
        <Box style={{ padding: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <Box
            style={{ cursor: 'pointer' }}
            onClick={() => setIsSidebarOpen(prev => !prev)}
          >
            {isSidebarOpen ? <ChevronLeft size={22} color='#000' /> : <ChevronRight size={22} color='#000' />}
          </Box>
        </Box>
        <Box style={{ overflowY: 'auto', height: 'calc(100% - 40px)' }}>
          {isSidebarOpen && cards.map((card: Card) => (
            <Box
              key={card.id}
              p="sm"
              style={{
                borderBottom: '1px solid #ddd',
                backgroundColor: selectedCardId === card.id ? '#e3f2fd' : undefined,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                overflowY: 'scroll',
              }}
              onClick={() => {
                setSelectedCardId(card.id);
                if (!cardConfigMap[card.id]) {
                  handleCardSelection(card.formId, card.id);
                }
              }}
            >
              {card.name}

            </Box>

          ))}
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        style={{
          flexGrow: 1,
          padding: '1rem',
          border: '1px solid #ccc',
          borderRadius: 8,
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        {loading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Loader size="xl" />
          </Box>
        ) : (
          <>
            <Group position="right" style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10 }}>
              <Button onClick={handleBack}>Back</Button>
            </Group>

            {currentCardState?.step === 'api' && currentCardState.apiSelectorPayload ? (
              <>
                <ApiSelector
                  formSections={currentCardState.apiSelectorPayload.formSections}
                  meta={currentCardState.apiSelectorPayload.meta}
                  widgetPanelDisabled={true}
                  onSave={handleSave}
                  onFormChange={handleFormChange}
                />
                <Box style={{ position: 'relative', bottom: '1rem', marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button onClick={finalizeAndSubmit}>Submit</Button>
                </Box>
                <GenericPopUp
                  type={popupType}
                  message={popupMessage}
                  setOpenGenericModal={
                    popupType === 'Success'
                      ? () => {
                        setOpenGenericModal(false);
                        window.history.back();
                      }
                      : setOpenGenericModal
                  }
                  openGenericModal={openGenericModal}
                />
              </>
            ) : currentCardState?.editFormConfig ? (
              <>
                {/* {currentCardState.unmatchedLabels.length > 0 && (
                  <Box mb="md">
                    <Box style={{ fontWeight: 'bold', marginBottom: '1rem' }}>Unmatched Labels:</Box>
                    <ScrollArea style={{ width: '100%' }}>
                      <Box style={{ display: 'flex', gap: '8px', padding: '4px 0' }}>
                        {currentCardState.unmatchedLabels.map((label, index) => (
                          <Badge key={index} color="red" size="lg" style={{ whiteSpace: 'nowrap' }}>
                            {label}
                          </Badge>
                        ))}
                      </Box>
                    </ScrollArea>
                  </Box>
                )} */}
                <Box style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                  <CreateForm
                    initialForm={filteredInitialForm}
                    widgetPanelDisabled={true}
                    onSave={handleSave}
                    onFormChange={handleFormChange}
                    mandatoryLabels={currentCardState?.mandatoryLabels || []}
                  />
                </Box>
                <Box style={{ position: 'relative', marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button onClick={finalizeAndSubmit}>Submit</Button>
                </Box>
                <GenericPopUp
                  type={popupType}
                  message={popupMessage}
                  setOpenGenericModal={
                    popupType === 'Success'
                      ? () => {
                        setOpenGenericModal(false);
                        window.history.back();
                      }
                      : setOpenGenericModal
                  }
                  openGenericModal={openGenericModal}
                />
              </>
            ) : (
              <div>Please select a card to edit the form.</div>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MdmFormSetUp;
